import { Component, Inject, OnInit, OnDestroy, HostListener, ViewChild, ElementRef, PLATFORM_ID } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { MessageService } from 'primeng/api';
import {
  AtualizarUnidadeMedidaPadraoDTO,
  UnidadeMedidaDeItemDTO,
  UnidadeMedidaDeItemFormDTO,
} from '../../models/configuracoes/unidade-de-medida.model';
import { ConfiguracaoGeralDTO } from '../../models/configuracoes/configuracao-geral.model';
import { UnidadeMedidaDeItemService } from '../../services/configuracoes/unidade-de-medida.service';
import { ApiService } from '../../services/api.service';
import { Router } from '@angular/router';
import { LangChangeEvent, TranslateService } from '@ngx-translate/core';
import { Subscription } from 'rxjs';
import { TableColumn } from '../../models/shared/table-column.model';

interface PageEvent {
  first: number;
  rows: number;
  page: number;
  pageCount: number;
}

@Component({
  selector: 'app-configuracao-unidade-de-medida',
  templateUrl: './configuracao-unidade-de-medida.component.html',
  styleUrl: './configuracao-unidade-de-medida.component.scss',
  providers: [MessageService],
})
export class ConfiguracaoUnidadeDeMedidaComponent implements OnInit, OnDestroy {
  products: UnidadeMedidaDeItemDTO[] = [];
  productsOriginal: UnidadeMedidaDeItemDTO[] = [];
  first3 = 0;
  rows3 = 10;
  totalRecords = 0;
  loading = true;
  lastfield = 'id';
  lastSort = 'asc';
  searchNome = '';

  /** Unidade padrao da empresa */
  unidadePadraoId: number | null = null;

  /** ID da ConfiguracaoGeral da empresa (usado como path param do PATCH) */
  configuracaoGeralId: number | null = null;

  /** Flag de perfil master — controla visibilidade da acao "Definir como padrao" */
  isMaster = false;

  /** Guard anti-duplo-clique: ID da unidade sendo processada */
  definindoPadraoId: number | null = null;

  pageSizeOptions = [
    { label: 5, value: 5 },
    { label: 10, value: 10 },
    { label: 20, value: 20 },
    { label: 120, value: 120 },
  ];

  form: UnidadeMedidaDeItemFormDTO = this.createEmptyForm();
  isFormSubmitted = false;
  submitting = false;
  activeTabIndex: number = 1;

  // ViewChild para foco no campo de busca (F2)
  @ViewChild('searchInput') searchInputRef!: ElementRef<HTMLInputElement>;

  // ViewChild para foco no botao de confirmar exclusao
  @ViewChild('deleteConfirmBtn') deleteConfirmBtnRef!: ElementRef<HTMLButtonElement>;


  // Dialog de exclusao
  deleteDialogVisible = false;
  deleteItemId: string | null = null;

  // Configuracao de colunas visiveis
  readonly STORAGE_KEY = 'unidade-de-medida-visible-columns';

  columns: TableColumn[] = [
    { key: 'codigoDTE', labelKey: 'unidade-de-medida.labels.codigo_dte', visible: true, fixed: false, defaultVisible: true },
    { key: 'tipoUnidade', labelKey: 'unidade-de-medida.labels.tipo_de_unidade', visible: true, fixed: false, defaultVisible: true },
    { key: 'nome', labelKey: 'unidade-de-medida.labels.nome', visible: true, fixed: true, defaultVisible: true },
    { key: 'casasDecimais', labelKey: 'unidade-de-medida.labels.casas_decimais', visible: true, fixed: false, defaultVisible: true },
    { key: 'status', labelKey: 'unidade-de-medida.labels.status', visible: true, fixed: false, defaultVisible: true },
    { key: 'criadoEm', labelKey: 'unidade-de-medida.labels.data_inclusao', visible: true, fixed: false, defaultVisible: true },
    { key: 'acoes', labelKey: 'unidade-de-medida.labels.acoes', visible: true, fixed: true, defaultVisible: true },
  ];

  // Textos traduzidos
  texts = {
    titulo: '',
    subtitulo: '',
    dados_unidade: '',
    unidades_de_medida: '',
    nenhuma_unidade: '',
    adicione_nova_unidade: '',
    confirmacao: '',
    confirmar_exclusao: '',
    carregando: '',
    aba_cadastro: '',
    aba_lista: '',
  };

  labels = {
    tipo_de_unidade: '',
    nome: '',
    casas_decimais: '',
    codigo_dte: '',
    status: '',
    data_inclusao: '',
    acoes: '',
    itens_por_pagina: '',
  };

  placeholders = {
    tipo_de_unidade: '',
    nome: '',
    casas_decimais: '',
    codigo_dte: '',
    pesquisar: '',
  };

  botoes = {
    adicionar: '',
    salvar: '',
    limpar: '',
    ativo: '',
    inativo: '',
    confirmar: '',
    cancelar: '',
    nova_unidade: '',
  };

  tooltips = {
    editar: '',
    excluir: '',
    definir_padrao: '',
    unidade_padrao: '',
  };

  private langChangeSub!: Subscription;

  constructor(
    private messageService: MessageService,
    private unidadeMedidaDeItemService: UnidadeMedidaDeItemService,
    private apiService: ApiService,
    private router: Router,
    private translate: TranslateService,
    @Inject(PLATFORM_ID) private platformId: object
  ) {}

  ngOnInit() {
    this.loadColumnPreferences();
    this.loadTranslations();
    this.verificarPerfilMaster();
    this.buscarUnidadePadrao();
    this.findUnidadeDeMedida();

    // Atualiza labels quando idioma muda
    this.langChangeSub = this.translate.onLangChange.subscribe((event: LangChangeEvent) => {
      this.loadTranslations();
    });
  }

  ngOnDestroy() {
    if (this.langChangeSub) {
      this.langChangeSub.unsubscribe();
    }
  }

  /**
   * Le a flag categoriaAcessoMaster do storage (session > local).
   * Mesmo padrao usado pelo masterAccessGuard e menu-lateral.
   */
  private verificarPerfilMaster(): void {
    if (!isPlatformBrowser(this.platformId)) {
      this.isMaster = false;
      return;
    }
    const valor =
      sessionStorage.getItem('categoriaAcessoMaster') ||
      localStorage.getItem('categoriaAcessoMaster');
    this.isMaster = valor === 'true';
  }

  /** Busca a unidade de medida padrao da empresa via configuracaoGeral */
  private buscarUnidadePadrao(): void {
    this.apiService.read<any>('/configuracaoGeral').subscribe({
      next: (response: any) => {
        const cfg = response?.content?.[0];
        if (cfg) {
          this.configuracaoGeralId = cfg.id ?? null;
          this.unidadePadraoId = cfg.unidadeMedidaPadraoId || null;
        } else {
          this.configuracaoGeralId = null;
          this.unidadePadraoId = null;
        }
      },
      error: () => {
        this.configuracaoGeralId = null;
        this.unidadePadraoId = null;
      }
    });
  }

  /** Verifica se uma unidade e a padrao da empresa */
  ehPadrao(product: UnidadeMedidaDeItemDTO): boolean {
    return product?.id != null && product.id === this.unidadePadraoId;
  }

  /**
   * Define a unidade da linha como padrao da empresa.
   * Apenas usuarios master — o backend tambem valida (retorna 422).
   * Atualizacao local apos sucesso (sem refetch da lista).
   * Guard anti-duplo-clique via definindoPadraoId.
   */
  definirComoPadrao(product: UnidadeMedidaDeItemDTO): void {
    if (!product?.id || this.definindoPadraoId === product.id) {
      return;
    }

    if (this.configuracaoGeralId == null) {
      this.messageService.add({
        severity: 'error',
        summary: this.translate.instant('common.erro'),
        detail: this.translate.instant('configuracao-unidade-de-medida.texts.erro_config_nao_carregada'),
      });
      return;
    }

    const dto: AtualizarUnidadeMedidaPadraoDTO = {
      unidadeMedidaDeItemId: product.id,
    };

    this.definindoPadraoId = product.id;

    this.unidadeMedidaDeItemService
      .definirComoPadrao(this.configuracaoGeralId, dto)
      .subscribe({
        next: (response: ConfiguracaoGeralDTO) => {
          this.unidadePadraoId = response?.unidadeMedidaPadraoId ?? null;
          this.definindoPadraoId = null;
          this.messageService.add({
            severity: 'success',
            summary: this.translate.instant('common.sucesso'),
            detail: this.translate.instant('configuracao-unidade-de-medida.texts.sucesso_definir_padrao'),
          });
        },
        error: () => {
          this.definindoPadraoId = null;
        },
      });
  }

  /** Mapeia status HTTP para mensagem amigavel via i18n. */
  private tratarErroDefinirPadrao(err: any): void {
    const prefix = 'configuracao-unidade-de-medida.texts';
    let detail: string;

    switch (err?.status) {
      case 400:
        detail =
          err?.error?.message ||
          this.translate.instant(`${prefix}.erro_definir_padrao`);
        break;
      case 403:
      case 404:
        detail = this.translate.instant(`${prefix}.erro_definir_padrao`);
        break;
      case 422:
        detail = this.translate.instant(`${prefix}.erro_master_obrigatorio`);
        break;
      default:
        detail = this.translate.instant('common.erro');
    }

    this.messageService.add({
      severity: 'error',
      summary: this.translate.instant('common.erro'),
      detail,
    });
  }

  onTabChange(event: { index: number }): void {
    if (event.index === 1) {
      this.texts.subtitulo = this.translate.instant('unidade-de-medida.texts.subtitulo_lista');
    } else {
      this.texts.subtitulo = this.translate.instant('unidade-de-medida.texts.subtitulo');
    }
  }

  private loadTranslations() {
    const isListaAtiva = this.activeTabIndex === 1;
    this.texts = {
      titulo: this.translate.instant('unidade-de-medida.texts.add_nova_unidade_de_medida'),
      subtitulo: this.translate.instant(
        isListaAtiva ? 'unidade-de-medida.texts.subtitulo_lista' : 'unidade-de-medida.texts.subtitulo'
      ),
      dados_unidade: this.translate.instant('unidade-de-medida.texts.dados_unidade'),
      unidades_de_medida: this.translate.instant('unidade-de-medida.texts.unidades_de_medida'),
      nenhuma_unidade: this.translate.instant('unidade-de-medida.texts.nenhuma_unidade'),
      adicione_nova_unidade: this.translate.instant('unidade-de-medida.texts.adicione_nova_unidade'),
      confirmacao: this.translate.instant('unidade-de-medida.texts.confirmacao'),
      confirmar_exclusao: this.translate.instant('unidade-de-medida.texts.confirmar_exclusao'),
      carregando: this.translate.instant('unidade-de-medida.texts.carregando'),
      aba_cadastro: this.translate.instant('unidade-de-medida.texts.aba_cadastro'),
      aba_lista: this.translate.instant('unidade-de-medida.texts.aba_lista'),
    };

    this.labels = {
      tipo_de_unidade: this.translate.instant('unidade-de-medida.labels.tipo_de_unidade'),
      nome: this.translate.instant('unidade-de-medida.labels.nome'),
      casas_decimais: this.translate.instant('unidade-de-medida.labels.casas_decimais'),
      codigo_dte: this.translate.instant('unidade-de-medida.labels.codigo_dte'),
      status: this.translate.instant('unidade-de-medida.labels.status'),
      data_inclusao: this.translate.instant('unidade-de-medida.labels.data_inclusao'),
      acoes: this.translate.instant('unidade-de-medida.labels.acoes'),
      itens_por_pagina: this.translate.instant('unidade-de-medida.labels.itens_por_pagina'),
    };

    this.placeholders = {
      tipo_de_unidade: this.translate.instant('unidade-de-medida.placeholders.tipo_de_unidade'),
      nome: this.translate.instant('unidade-de-medida.placeholders.nome'),
      casas_decimais: this.translate.instant('unidade-de-medida.placeholders.casas_decimais'),
      codigo_dte: this.translate.instant('unidade-de-medida.placeholders.codigo_dte'),
      pesquisar: this.translate.instant('unidade-de-medida.placeholders.pesquisar'),
    };

    this.botoes = {
      adicionar: this.translate.instant('unidade-de-medida.botoes.adicionar'),
      salvar: this.translate.instant('unidade-de-medida.botoes.salvar'),
      limpar: this.translate.instant('unidade-de-medida.botoes.limpar'),
      ativo: this.translate.instant('unidade-de-medida.labels.ativo'),
      inativo: this.translate.instant('unidade-de-medida.labels.inativo'),
      confirmar: this.translate.instant('unidade-de-medida.botoes.confirmar'),
      cancelar: this.translate.instant('unidade-de-medida.botoes.cancelar'),
      nova_unidade: this.translate.instant('unidade-de-medida.botoes.nova_unidade'),
    };

    this.tooltips = {
      editar: this.translate.instant('unidade-de-medida.tooltips.editar'),
      excluir: this.translate.instant('unidade-de-medida.tooltips.excluir'),
      definir_padrao: this.translate.instant('configuracao-unidade-de-medida.tooltips.definir_padrao'),
      unidade_padrao: this.translate.instant('configuracao-unidade-de-medida.tooltips.unidade_padrao'),
    };
  }


  // =========================================================================
  // Tabela
  // =========================================================================

  findUnidadeDeMedida(
    page: number = 0,
    size: number = this.rows3,
    sort: string = 'id,asc'
  ) {
    this.loading = true;
    this.unidadeMedidaDeItemService
      .get('/unidadeMedidaItem/listarTodas', { page, size, sort })
      .subscribe({
        next: (response) => {
          this.productsOriginal = response.content || [];
          this.products = [...this.productsOriginal];
          this.totalRecords = response.totalElements || this.productsOriginal.length;
          this.first3 = page * size;
          this.loading = false;

          // Reaplicar filtro local se houver termo de busca ativo
          if (this.searchNome && this.searchNome.trim().length > 0) {
            this.filtrarLocal(this.searchNome.trim());
          }
        },
        error: () => {
          this.productsOriginal = [];
          this.products = [];
          this.totalRecords = 0;
          this.loading = false;
        }
      });
  }

  onSearchChange(value: string): void {
    this.searchNome = value || '';
    if (this.searchNome.trim().length > 0) {
      this.first3 = 0;
      this.filtrarLocal(this.searchNome.trim());
    } else {
      this.products = [...this.productsOriginal];
      this.totalRecords = this.productsOriginal.length;
    }
  }

  /** Filtra unidades localmente buscando em todas as colunas visiveis */
  private filtrarLocal(term: string): void {
    const termLower = term.toLowerCase();

    this.products = this.productsOriginal.filter((product: any) => {
      if (this.isColumnVisible('codigoDTE') && product.codigoDTE != null) {
        if (String(product.codigoDTE).includes(termLower)) return true;
      }
      if (this.isColumnVisible('tipoUnidade') && product.tipoUnidade) {
        if (product.tipoUnidade.toLowerCase().includes(termLower)) return true;
      }
      if (this.isColumnVisible('nome') && product.nome) {
        if (product.nome.toLowerCase().includes(termLower)) return true;
      }
      if (this.isColumnVisible('casasDecimais') && product.casasDecimais != null) {
        if (String(product.casasDecimais).includes(termLower)) return true;
      }
      return false;
    });

    this.totalRecords = this.products.length;
  }

  onPageChange3(event: any): void {
    const page = event.page;
    const size = event.rows;
    this.findUnidadeDeMedida(page, size, `${this.lastfield},${this.lastSort}`);
  }

  onRowsPerPageChange(): void {
    this.first3 = 0;
    this.findUnidadeDeMedida(0, this.rows3, `${this.lastfield},${this.lastSort}`);
  }

  onSortChange(event: any): void {
    if (!event.field) return;

    const field = event.field;
    const order = event.order === 1 ? 'asc' : 'desc';

    // Evitar chamada duplicada se mesmo campo e mesma direcao
    if (this.lastfield === field && this.lastSort === order) return;

    this.lastfield = field;
    this.lastSort = order;

    // Ordenar no backend (todos os registros do banco)
    this.first3 = 0;
    this.findUnidadeDeMedida(0, this.rows3, `${field},${order}`);
  }

  formatarDataEHora(dataISO: string): string {
    const data = new Date(dataISO);
    if (isNaN(data.getTime())) return '';
    const dia = String(data.getDate()).padStart(2, '0');
    const mes = String(data.getMonth() + 1).padStart(2, '0');
    const ano = data.getFullYear();
    const horas = String(data.getHours()).padStart(2, '0');
    const minutos = String(data.getMinutes()).padStart(2, '0');
    return `${dia}/${mes}/${ano} ${horas}:${minutos}`;
  }

  // =========================================================================
  // Colunas
  // =========================================================================

  isColumnVisible(key: string): boolean {
    const col = this.columns.find(c => c.key === key);
    return col ? col.visible : true;
  }

  get visibleColumnCount(): number {
    return this.columns.filter(c => c.visible).length;
  }

  onColumnsChange(columns: TableColumn[]): void {
    this.columns = [...columns];
  }

  private loadColumnPreferences(): void {
    try {
      const stored = localStorage.getItem(this.STORAGE_KEY);
      if (stored) {
        const visibleKeys = JSON.parse(stored) as string[];
        this.columns.forEach(col => {
          if (!col.fixed) {
            col.visible = visibleKeys.includes(col.key);
          }
        });
      }
    } catch (e) {
      console.warn('Erro ao carregar preferencias de colunas:', e);
    }
  }

  // =========================================================================
  // Formulario
  // =========================================================================

  submit() {
    if (this.submitting) return;
    this.isFormSubmitted = true;
    if (!this.form.tipoUnidade || !this.form.nome) {
      this.messageService.add({
        severity: 'error',
        summary: this.translate.instant('unidade-de-medida.texts.erro'),
        detail: this.translate.instant('unidade-de-medida.texts.campos_obrigatorios'),
      });
      return;
    }
    this.adicionarUnidadeMedidaDeItem();
  }

  adicionarUnidadeMedidaDeItem() {
    const body = {
      tipoUnidade: this.form.tipoUnidade,
      nome: this.form.nome,
      ativo: this.form.ativo,
      casasDecimais: this.form.casasDecimais,
      codigoDTE: this.form.codigoDTE,
    };
    this.submitting = true;
    this.unidadeMedidaDeItemService
      .post(`/unidadeMedidaItem`, body)
      .subscribe({
        next: () => {
          this.submitting = false;
          this.messageService.add({
            severity: 'success',
            summary: this.translate.instant('unidade-de-medida.texts.sucesso'),
            detail: this.translate.instant('unidade-de-medida.texts.unidade_adicionada_sucesso'),
          });
          this.form = this.createEmptyForm();
          this.isFormSubmitted = false;
          this.findUnidadeDeMedida();
          this.activeTabIndex = 1;
        },
        error: () => {
          this.submitting = false;
        },
      });
  }

  alterarUnidadeMedidaDeItem() {
    if (this.submitting) return;
    this.isFormSubmitted = true;
    if (!this.form.tipoUnidade || !this.form.nome) {
      this.messageService.add({
        severity: 'error',
        summary: this.translate.instant('unidade-de-medida.texts.erro'),
        detail: this.translate.instant('unidade-de-medida.texts.campos_obrigatorios'),
      });
      return;
    }

    const body = {
      tipoUnidade: this.form.tipoUnidade,
      nome: this.form.nome,
      ativo: this.form.ativo,
      casasDecimais: this.form.casasDecimais,
      codigoDTE: this.form.codigoDTE,
    };
    this.submitting = true;
    this.unidadeMedidaDeItemService
      .put(`/unidadeMedidaItem/${this.form.id}`, body)
      .subscribe({
        next: () => {
          this.submitting = false;
          this.messageService.add({
            severity: 'success',
            summary: this.translate.instant('unidade-de-medida.texts.sucesso'),
            detail: this.translate.instant('unidade-de-medida.texts.unidade_editada_sucesso'),
          });
          this.form = this.createEmptyForm();
          this.isFormSubmitted = false;
          this.findUnidadeDeMedida();
          this.activeTabIndex = 1;
        },
        error: () => {
          this.submitting = false;
        },
      });
  }

  deletarUnidadeMedidaDeItem(id: string) {
    this.deleteItemId = id;
    this.deleteDialogVisible = true;
  }

  onDeleteDialogShow() {
    setTimeout(() => {
      this.deleteConfirmBtnRef?.nativeElement?.focus();
    }, 50);
  }

  confirmarExclusao() {
    if (!this.deleteItemId) return;

    this.unidadeMedidaDeItemService
      .delete(`/unidadeMedidaItem/${this.deleteItemId}`)
      .subscribe({
        next: () => {
          this.messageService.add({
            severity: 'success',
            summary: this.translate.instant('unidade-de-medida.texts.sucesso'),
            detail: this.translate.instant('unidade-de-medida.texts.unidade_excluida_sucesso'),
          });
          this.deleteDialogVisible = false;
          this.deleteItemId = null;
          this.findUnidadeDeMedida();
        },
        error: () => {
          this.deleteDialogVisible = false;
          this.deleteItemId = null;
        },
      });
  }

  createEmptyForm(): UnidadeMedidaDeItemFormDTO {
    return {
      ativo: true,
    } as UnidadeMedidaDeItemFormDTO;
  }

  limparForm() {
    this.form = this.createEmptyForm();
    this.isFormSubmitted = false;
  }

  alternarStatus(product: UnidadeMedidaDeItemDTO) {
    if (!product.id) return;

    const body: UnidadeMedidaDeItemDTO = {
      ...product,
      ativo: product.ativo,
    };

    this.unidadeMedidaDeItemService.ativarOuInativar(product.id, body).subscribe({
      next: () => {
        this.messageService.add({
          severity: 'success',
          summary: this.translate.instant('unidade-de-medida.texts.sucesso'),
          detail: product.ativo
            ? this.translate.instant('unidade-de-medida.texts.unidade_ativada_sucesso')
            : this.translate.instant('unidade-de-medida.texts.unidade_inativada_sucesso'),
        });
      },
      error: () => {
        // Reverter o status em caso de erro
        product.ativo = !product.ativo;
      },
    });
  }

  editarUnidadeMedidaDeItem(event: UnidadeMedidaDeItemFormDTO) {
    this.form = { ...event };
    this.activeTabIndex = 0;
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }

  novaUnidade(): void {
    this.limparForm();
    this.activeTabIndex = 0;
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }

  // Atalhos de teclado
  @HostListener('document:keydown', ['$event'])
  onGlobalKeydown(ev: KeyboardEvent): void {
    // Atalhos do dialog de exclusao
    if (this.deleteDialogVisible) {
      if (ev.key === 'Enter') {
        ev.preventDefault();
        this.confirmarExclusao();
        return;
      }
      if (ev.key === 'Escape') {
        ev.preventDefault();
        this.deleteDialogVisible = false;
        return;
      }
      return;
    }

    // F2: focar no campo de busca
    if (ev.key === 'F2') {
      ev.preventDefault();
      setTimeout(() => {
        this.searchInputRef?.nativeElement?.focus();
      }, 100);
      return;
    }

    // Ctrl+Enter: adicionar ou salvar unidade de medida
    if (ev.ctrlKey && ev.key === 'Enter') {
      ev.preventDefault();
      this.form.id ? this.alterarUnidadeMedidaDeItem() : this.submit();
      return;
    }
  }
}
